import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { API_ROOT } from '../config';
import LoadingSpinner from './ui/loading-spinner';
import SEO from '@/components/seo';

const PALETTE = {
  gunmetal: '#243837',
};

export default function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const hasVerifiedRef = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link.');
      return;
    }

    // Prevent duplicate verification requests using ref
    if (hasVerifiedRef.current) {
      console.log('⚠️ Verification already in progress, skipping duplicate request');
      return;
    }
    hasVerifiedRef.current = true;

    console.log('🔍 Verifying email with token:', token);

    // Verify with backend
    fetch(`${API_ROOT}/verify-email?token=${token}`)
      .then(async response => {
        const data = await response.json();
        console.log('📧 Verification response:', { status: response.status, data });
        
        if (response.ok && data.success) {
          setStatus('success');
          setMessage(data.message);
        } else {
          setStatus('error');
          setMessage(data.message || 'Verification failed. Please try again.');
        }
      })
      .catch((error) => {
        console.error('❌ Verification error:', error);
        setStatus('error');
        setMessage('Verification failed. Please try again.');
      });
  }, [searchParams]);

  const handleContinue = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <SEO
        title="Verify Your Email"
        description="Confirm your email address to activate your BrandComply account."
        path="/verify"
      />
      <Card className="max-w-md w-full p-6 text-center">
        {status === 'loading' && (
          <>
            <LoadingSpinner size="lg" className="mb-4" />
            <h2 className="text-xl font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
              Verifying your email...
            </h2>
            <p style={{ color: `${PALETTE.gunmetal}80` }}>Please wait while we verify your email address.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="mx-auto mb-4 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Email Verified!
            </h2>
            <Button onClick={handleContinue} className="w-full">
              Continue to BrandComply
            </Button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="mx-auto mb-4 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Verification Failed
            </h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Button onClick={handleContinue} variant="outline" className="w-full">
                Go to Login
              </Button>
              <p className="text-sm text-gray-500">
                Need help? Contact info@brandcomply.ai
              </p>
            </div>
          </>
        )}
      </Card>
    </div>
  );
} 